import { useEffect, useRef, type MutableRefObject } from "react";
import L, { LatLngTuple } from "leaflet";
import { useMap } from "react-leaflet";
import "leaflet.markercluster";
import { renderToStaticMarkup } from "react-dom/server";
import { VictimPopupContent } from "./VictimPopupContent";
import { CustomMarkerPopupContent } from "./CustomMarkerPopupContent";
import type { CustomMarker } from "../../../context/CustomMarkersContext";
import { getMarkerIcon } from "../../../utils/map/markerHandler";
import type { VictimDTO } from "@shared/contracts";
import {
  attachPopupFrontHandlers,
  openPopupWithoutPan,
  reopenSavedPopupsFor,
  handleOpenRowPopups,
} from "../../../utils/map/popupHandler";
import {
  createClusterGroup,
  handleAnimationend,
  handleClusterclick,
  handleSpiderfied,
  handleUnspiderfied,
} from "../../../utils/map/clusterHandler";
import {
  DEFAULT_MARKER_COLOR,
  DRAWER_WIDTH_PX,
  POPUP_REOPEN_DELAY,
} from "../../../utils/constants";
import { useHighlight } from "../../../hooks/useHighlight";

type ColoredPoint = {
  position: LatLngTuple;
  color?: string;
  kind?: "missing" | "murder" | "unsolved" | "survived" | "custom";
  label?: string;
  payload?: VictimDTO;
  rowId?: number;
};

type Props = {
  points: ColoredPoint[];
  size?: number;
  persistedOpenIdsRef: MutableRefObject<Set<string>>;
};

const MAX_CLUSTER_RADIUS = 80;

// Renders victim popup as static HTML for Leaflet
function buildVictimPopup(v: VictimDTO, color = DEFAULT_MARKER_COLOR) {
  return renderToStaticMarkup(<VictimPopupContent v={v} color={color} />);
}

function buildCustomMarkerPopup(
  marker: CustomMarker,
  color = DEFAULT_MARKER_COLOR,
) {
  return renderToStaticMarkup(
    <CustomMarkerPopupContent marker={marker} color={color} />,
  );
}

// Groups points by their coordinates
function groupPoints(pts: ColoredPoint[]) {
  const map = new Map<
    string,
    { latlng: LatLngTuple; entries: ColoredPoint[] }
  >();
  for (const p of pts) {
    const key = `${p.position[0]},${p.position[1]}`;
    const g = map.get(key);
    g ? g.entries.push(p) : map.set(key, { latlng: p.position, entries: [p] });
  }
  return map;
}

export default function ClusterLayer({
  points,
  size = 60,
  persistedOpenIdsRef,
}: Props) {
  const map = useMap();
  const tearingDownRef = useRef(false);

  const { isHighlightMode, highlightedIds, highlightColor, toggleHighlight } =
    useHighlight();

  // Use refs so Leaflet click handlers always read the latest values
  // without needing to rebuild all markers on mode changes
  const isHighlightModeRef = useRef(isHighlightMode);
  isHighlightModeRef.current = isHighlightMode;
  const toggleHighlightRef = useRef(toggleHighlight);
  toggleHighlightRef.current = toggleHighlight;

  useEffect(() => {
    tearingDownRef.current = false;
    // Layer group for all markers/clusters
    const layerGroup = L.layerGroup();
    const iconBase = (process.env.PUBLIC_URL || "") + "/markers";

    type Tracked = {
      group: L.MarkerClusterGroup;
      sample: L.Marker;
      wasOpen: boolean;
    };
    const tracked: Tracked[] = [];
    const allMarkers: { marker: L.Marker; group?: L.MarkerClusterGroup }[] = [];

    let suppressAllCloseCleanup = false;

    // Attach/detach the detail-button click handler on a victim popup
    const attachDetailHandler = (
      marker: L.Marker,
      victim: VictimDTO,
      markerColor: string,
    ) => {
      const popupEl = marker.getPopup()?.getElement();
      if (!popupEl) return;
      const btn = popupEl.querySelector<HTMLElement>(
        '[data-action="victim-detail"]',
      );
      if (!btn) return;
      const handler = (e: Event) => {
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent("open-victim-detail", {
            detail: { victim, color: markerColor },
          }),
        );
      };
      (marker as any).__detailBtnHandler = handler;
      btn.addEventListener("click", handler);
    };

    const detachDetailHandler = (marker: L.Marker) => {
      const popupEl = marker.getPopup()?.getElement();
      if (!popupEl) return;
      const btn = popupEl.querySelector<HTMLElement>(
        '[data-action="victim-detail"]',
      );
      const handler = (marker as any).__detailBtnHandler as
        | ((e: Event) => void)
        | undefined;
      if (btn && handler) {
        btn.removeEventListener("click", handler);
        (marker as any).__detailBtnHandler = undefined;
      }
    };

    // Prevent popup cleanup during zoom
    const onZoomStart = () => {
      suppressAllCloseCleanup = true;
    };
    map.on("zoomstart", onZoomStart);

    // Group points by coordinates
    const grouped = groupPoints(points);

    grouped.forEach(({ latlng, entries }) => {
      if (entries.length > 1) {
        // Clustered markers
        const colors = new Set(
          entries.map((e) => e.color || DEFAULT_MARKER_COLOR),
        );
        const clusterGroup = createClusterGroup(
          colors,
          entries.length,
          size,
          MAX_CLUSTER_RADIUS,
        );

        const openIds = new Set<string>();

        // Create markers for each entry in the cluster
        const markers = entries.map(
          ({ color, kind, label, payload, rowId }) => {
            const vid = payload?.id ? String(payload.id) : undefined;
            const isHighlighted = vid ? highlightedIds.has(vid) : false;
            const m = L.marker(latlng, {
              icon: getMarkerIcon(
                iconBase,
                color || DEFAULT_MARKER_COLOR,
                kind,
                label,
                size,
                isHighlighted,
                highlightColor,
              ),
              zIndexOffset: isHighlighted ? 1000 : 0,
            });
            if (vid) (m as any).__victimId = vid;
            if (rowId != null) (m as any).__rowId = rowId;

            if (payload) {
              m.bindPopup(
                kind === "custom"
                  ? buildCustomMarkerPopup(
                      payload as unknown as CustomMarker,
                      color || DEFAULT_MARKER_COLOR,
                    )
                  : buildVictimPopup(payload, color || DEFAULT_MARKER_COLOR),
                {
                  closeButton: true,
                  autoPan: true,
                  offset: L.point(0, 0),
                  className: "victim-popup",
                  maxWidth: 320,
                  autoClose: false,
                  closeOnClick: false,
                },
              );
              attachPopupFrontHandlers(m, map);
              if (kind !== "custom" && payload) {
                m.on("popupopen", () =>
                  attachDetailHandler(
                    m,
                    payload,
                    color || DEFAULT_MARKER_COLOR,
                  ),
                );
                m.on("popupclose", () => detachDetailHandler(m));
              }
              if (vid) {
                // In highlight mode: toggle highlight and suppress popup
                m.on("click", () => {
                  if (isHighlightModeRef.current) {
                    toggleHighlightRef.current(vid);
                  }
                });
                m.on("popupopen", () => {
                  if (isHighlightModeRef.current) {
                    setTimeout(() => m.closePopup(), 0);
                    return;
                  }
                  openIds.add(vid);
                  persistedOpenIdsRef.current.add(vid);
                });
              } else {
                m.on("popupopen", () => {
                  if (vid) {
                    openIds.add(vid);
                    persistedOpenIdsRef.current.add(vid);
                  }
                });
              }
              m.on("popupclose", () => {
                if (!vid) return;
                if (suppressAllCloseCleanup || tearingDownRef.current) return;

                openIds.delete(vid);
                persistedOpenIdsRef.current.delete(vid);
              });
            }
            allMarkers.push({ marker: m, group: clusterGroup });
            return m;
          },
        );

        clusterGroup.addLayers(markers);
        layerGroup.addLayer(clusterGroup);

        // Track cluster state for popup restoration
        const track: Tracked = {
          group: clusterGroup,
          sample: markers[0],
          wasOpen: false,
        };
        clusterGroup.on(
          "spiderfied",
          handleSpiderfied(track, clusterGroup, openIds, reopenSavedPopupsFor),
        );
        clusterGroup.on("unspiderfied", handleUnspiderfied(track));
        tracked.push(track);

        clusterGroup.on(
          "clusterclick",
          handleClusterclick(
            tracked,
            clusterGroup,
            openIds,
            reopenSavedPopupsFor,
          ),
        );

        clusterGroup.on(
          "animationend",
          handleAnimationend(
            track,
            clusterGroup,
            openIds,
            reopenSavedPopupsFor,
          ),
        );
      } else {
        // Single marker
        const { color, kind, label, payload, rowId } = entries[0];
        const vid = payload?.id ? String(payload.id) : undefined;
        const isHighlighted = vid ? highlightedIds.has(vid) : false;
        const marker = L.marker(latlng, {
          icon: getMarkerIcon(
            iconBase,
            color || DEFAULT_MARKER_COLOR,
            kind,
            label,
            size,
            isHighlighted,
            highlightColor,
          ),
          zIndexOffset: isHighlighted ? 1000 : 0,
        });
        if (vid) (marker as any).__victimId = vid;
        if (rowId != null) (marker as any).__rowId = rowId;

        if (payload) {
          marker.bindPopup(
            kind === "custom"
              ? buildCustomMarkerPopup(
                  payload as unknown as CustomMarker,
                  color || DEFAULT_MARKER_COLOR,
                )
              : buildVictimPopup(payload, color || DEFAULT_MARKER_COLOR),
            {
              closeButton: true,
              autoPan: true,
              offset: L.point(0, 0),
              className: "victim-popup",
              maxWidth: 320,
              autoClose: false,
              closeOnClick: false,
            },
          );
          attachPopupFrontHandlers(marker, map);
          if (kind !== "custom") {
            marker.on("popupopen", () =>
              attachDetailHandler(
                marker,
                payload,
                color || DEFAULT_MARKER_COLOR,
              ),
            );
            marker.on("popupclose", () => detachDetailHandler(marker));
          }
          if (vid) {
            // In highlight mode: toggle highlight and suppress popup
            marker.on("click", () => {
              if (isHighlightModeRef.current) {
                toggleHighlightRef.current(vid);
              }
            });
            marker.on("popupopen", () => {
              if (isHighlightModeRef.current) {
                setTimeout(() => marker.closePopup(), 0);
                return;
              }
              persistedOpenIdsRef.current.add(vid);
            });
            marker.on("popupclose", () => {
              if (suppressAllCloseCleanup || tearingDownRef.current) return;
              persistedOpenIdsRef.current.delete(vid);
            });
          }
        }
        layerGroup.addLayer(marker);
        allMarkers.push({ marker, group: undefined });
      }
    });

    // Restore open popups after render
    setTimeout(() => {
      const ids = persistedOpenIdsRef.current;
      if (!ids.size) return;
      const byGroup = new Map<L.MarkerClusterGroup | undefined, L.Marker[]>();
      for (const { marker, group } of allMarkers) {
        const id = (marker as any).__victimId;
        if (id && ids.has(id)) {
          const arr = byGroup.get(group) ?? [];
          arr.push(marker);
          byGroup.set(group, arr);
        }
      }

      byGroup.forEach((markers, group) => {
        if (!group) {
          markers.forEach((m) =>
            setTimeout(() => openPopupWithoutPan(m), POPUP_REOPEN_DELAY),
          );
          return;
        }
        const parent: any = (group as any).getVisibleParent(markers[0]);
        parent?.spiderfy?.();
        setTimeout(
          () => markers.forEach((m) => openPopupWithoutPan(m)),
          POPUP_REOPEN_DELAY,
        );
      });
    }, 0);

    // Listen for row popup events (open/close all for a row)
    const openRowPopupsHandler = handleOpenRowPopups(allMarkers, map);
    window.addEventListener(
      "open-popups-for-row",
      openRowPopupsHandler as EventListener,
    );

    const fitRowMarkersHandler = (ev: Event) => {
      const rowId = (ev as CustomEvent)?.detail?.rowId;
      if (rowId == null) return;

      const rowMarkerLatLngs = allMarkers
        .filter(({ marker }) => (marker as any).__rowId === rowId)
        .map(({ marker }) => marker.getLatLng());

      if (!rowMarkerLatLngs.length) return;

      if (rowMarkerLatLngs.length === 1) {
        const singleMaxZoom = window.innerWidth < 768 ? 7 : 8;
        map.flyTo(rowMarkerLatLngs[0], Math.max(map.getZoom(), singleMaxZoom), {
          animate: true,
          duration: 0.6,
        });
        return;
      }

      const isMobile = window.innerWidth < 768;
      const leftPad = isMobile ? 16 : DRAWER_WIDTH_PX;
      const pad = isMobile ? 16 : 24;
      const bounds = L.latLngBounds(rowMarkerLatLngs);
      map.fitBounds(bounds, {
        paddingTopLeft: [leftPad, pad],
        paddingBottomRight: [pad, pad],
        maxZoom: isMobile ? 7 : 10,
        animate: true,
      });
    };

    window.addEventListener(
      "fit-markers-for-row",
      fitRowMarkersHandler as EventListener,
    );

    const fitHighlightedMarkersHandler = (ev: Event) => {
      const ids: string[] = (ev as CustomEvent)?.detail?.ids ?? [];
      if (!ids.length) return;
      const idsSet = new Set(ids);
      const latLngs = allMarkers
        .filter(({ marker }) => {
          const vid = (marker as any).__victimId;
          return vid && idsSet.has(vid);
        })
        .map(({ marker }) => marker.getLatLng());
      if (!latLngs.length) return;
      const isMobile = window.innerWidth < 768;
      const leftPad = isMobile ? 16 : DRAWER_WIDTH_PX;
      const pad = isMobile ? 16 : 24;
      if (latLngs.length === 1) {
        const singleMaxZoom = isMobile ? 7 : 8;
        map.flyTo(latLngs[0], Math.max(map.getZoom(), singleMaxZoom), {
          animate: true,
          duration: 0.6,
        });
        return;
      }
      map.fitBounds(L.latLngBounds(latLngs), {
        paddingTopLeft: [leftPad, pad],
        paddingBottomRight: [pad, pad],
        maxZoom: isMobile ? 7 : 10,
        animate: true,
      });
    };

    window.addEventListener(
      "fit-highlighted-markers",
      fitHighlightedMarkersHandler as EventListener,
    );

    // Reset cleanup flag after zoom
    const onZoomEnd = () => {
      suppressAllCloseCleanup = false;
    };
    map.on("zoomend", onZoomEnd);

    // Add layer group to map
    map.addLayer(layerGroup);

    // Cleanup on unmount
    return () => {
      tearingDownRef.current = true;
      map.removeLayer(layerGroup);
      layerGroup.clearLayers();
      map.off("zoomstart", onZoomStart);
      map.off("zoomend", onZoomEnd);
      window.removeEventListener(
        "open-popups-for-row",
        openRowPopupsHandler as EventListener,
      );
      window.removeEventListener(
        "fit-markers-for-row",
        fitRowMarkersHandler as EventListener,
      );
      window.removeEventListener(
        "fit-highlighted-markers",
        fitHighlightedMarkersHandler as EventListener,
      );
    };
  }, [map, points, size, persistedOpenIdsRef, highlightedIds, highlightColor]);

  return null;
}
